import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '../../Servicios/supabaseClient.js';
import { Button, Card, Icon, Progress, Stat, StatusPill } from '../../Components/ui/ui.jsx';
import { downloadInvoice } from './storage.js';
import {
    arcaState, formatDate, formatMoney, formatWhen, invoiceTone, isLogOk, monthLabel,
} from './format.js';

const BANNERS = {
    pendiente_validacion: {
        tone: 'warn',
        title: 'Estamos validando tus credenciales de ARCA',
        text: 'Suele tardar unos minutos. Mientras tanto no se emiten facturas.',
    },
    error: {
        tone: 'danger',
        title: 'No pudimos conectarnos con ARCA',
        text: 'Tu clave fiscal no es válida o cambió. Revisala y volvé a conectar.',
        cta: 'Revisar conexión',
    },
    unconfigured: {
        tone: 'danger',
        title: 'Conectá tu cuenta de ARCA para empezar a facturar',
        text: 'Necesitamos tu CUIT y clave fiscal. Se guardan cifradas.',
        cta: 'Configurar ahora',
    },
};

export default function Resumen() {
    const { user, profile, plan, subscription, afipStatus, notify } = useOutletContext();

    const [loading, setLoading] = useState(true);
    const [activeFlows, setActiveFlows] = useState(0);
    const [monthLogs, setMonthLogs] = useState({ count: 0, hours: 0, okRate: null });
    const [monthInvoices, setMonthInvoices] = useState([]);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [activity, setActivity] = useState([]);
    const [teamCount, setTeamCount] = useState(0);
    const [accounts, setAccounts] = useState([]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

            try {
                const [flowsRes, logsRes, monthLogsRes, monthInvRes, recentInvRes, teamRes, accountsRes] = await Promise.all([
                    supabase.from('workflows').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('is_active', true),
                    supabase.from('execution_logs').select('*').eq('user_id', user.id).order('executed_at', { ascending: false }).limit(5),
                    supabase.from('execution_logs').select('horas_ahorradas, status', { count: 'exact' }).eq('user_id', user.id).gte('executed_at', firstDay).lte('executed_at', lastDay),
                    supabase.from('invoices').select('created_at, status').eq('user_id', user.id).gte('created_at', firstDay).lte('created_at', lastDay),
                    supabase.from('invoices').select('id, created_at, concepto, monto, status, file_path').eq('user_id', user.id).order('created_at', { ascending: false }).limit(5),
                    supabase.from('authorized_emails').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
                    supabase.from('linked_accounts').select('id, platform, reference_name').eq('user_id', user.id).order('created_at', { ascending: false }),
                ]);
                if (cancelled) return;

                const firstError = [flowsRes, logsRes, monthLogsRes, monthInvRes, recentInvRes].find((r) => r.error);
                if (firstError) throw firstError.error;

                setActiveFlows(flowsRes.count || 0);
                setActivity(logsRes.data || []);

                const logs = monthLogsRes.data || [];
                const hours = logs.reduce((acc, l) => acc + (Number(l.horas_ahorradas) || 0), 0);
                const okCount = logs.filter((l) => isLogOk(l.status)).length;
                setMonthLogs({
                    count: monthLogsRes.count ?? logs.length,
                    hours: Math.round(hours * 10) / 10,
                    okRate: logs.length > 0 ? Math.round((okCount / logs.length) * 100) : null,
                });

                setMonthInvoices(monthInvRes.data || []);
                setRecentInvoices(recentInvRes.data || []);
                setTeamCount(teamRes.count || 0);
                setAccounts(accountsRes.data || []);
            } catch (err) {
                console.error('Error cargando el resumen:', err.message);
                notify('No pudimos cargar todas las métricas. Recargá la página.', 'danger');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [user, notify]);

    const chart = useMemo(() => {
        const now = new Date();
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const today = now.getDate();
        const counts = new Array(daysInMonth).fill(0);
        monthInvoices.forEach((inv) => {
            if (inv.status !== 'completada') return;
            const i = new Date(inv.created_at).getDate() - 1;
            if (counts[i] !== undefined) counts[i]++;
        });

        const peak = Math.max(...counts, 1);
        const max = Math.max(5, Math.ceil(peak / 5) * 5);
        const labelDays = new Set([1, 5, 10, 15, 20, 25, daysInMonth]);

        // Promedio por día hábil transcurrido (lunes a viernes)
        let businessDays = 0;
        let businessTotal = 0;
        for (let d = 1; d <= today; d++) {
            const wd = new Date(now.getFullYear(), now.getMonth(), d).getDay();
            if (wd !== 0 && wd !== 6) {
                businessDays++;
                businessTotal += counts[d - 1];
            }
        }
        const avg = businessDays > 0 ? businessTotal / businessDays : 0;

        return {
            max,
            avg: avg.toLocaleString('es-AR', { maximumFractionDigits: 1 }),
            bars: counts.map((v, i) => {
                const day = i + 1;
                return {
                    day,
                    value: v,
                    isToday: day === today,
                    isFuture: day > today,
                    height: day > today ? 2 : Math.max(2, Math.round((v / max) * 150)),
                    label: labelDays.has(day) || day === today ? String(day) : '',
                };
            }),
        };
    }, [monthInvoices]);

    const invoiceStats = useMemo(() => {
        const done = monthInvoices.filter((i) => i.status === 'completada').length;
        const pending = monthInvoices.filter((i) => i.status !== 'completada' && i.status !== 'error').length;
        return { done, pending };
    }, [monthInvoices]);

    const arca = arcaState(afipStatus);
    const banner = afipStatus !== 'ok' ? BANNERS[afipStatus] || BANNERS.unconfigured : null;
    const maxFlows = plan?.max_flujos || 0;
    const month = monthLabel();

    const accountNames = accounts.map((a) => a.platform && a.platform !== 'Otros' ? a.platform : a.reference_name).filter(Boolean);
    const accountsSub = accounts.length === 0
        ? 'Todavía no vinculaste ninguna'
        : `${accounts.length} ${accounts.length === 1 ? 'cuenta' : 'cuentas'}: ${accountNames.slice(0, 3).join(', ')}${accountNames.length > 3 ? '…' : ''}`;

    const subStatus = subscription?.status;
    const subTone = subStatus === 'activo' ? 'ok' : subStatus === 'Por vencer' ? 'warn' : 'danger';

    return (
        <div className="page">
            <header className="pageHeader">
                <div className="pageHeaderText">
                    <h1 className="pageTitle">Hola, {profile?.nombre || 'de nuevo'}</h1>
                    <p className="pageSubtitle">
                        {[profile?.empresa, profile?.cuit && `CUIT ${profile.cuit}`, month].filter(Boolean).join(' · ')}
                    </p>
                </div>
                <div className="pageActions">
                    <StatusPill tone={arca.tone}>{arca.label}</StatusPill>
                    <Link to="/dashboard/exportar" className="qn-btn qn-btn-primary qn-btn-md">Exportar ventas</Link>
                </div>
            </header>

            {banner && (
                <div className={`banner banner-${banner.tone}`} role="status">
                    <Icon name="alert" size={20} className="bannerIcon" />
                    <div className="bannerText">
                        <span className="bannerTitle">{banner.title}</span>
                        <span className="bannerBody">{banner.text}</span>
                    </div>
                    {banner.cta && (
                        <Link to="/dashboard/arca" className="qn-btn qn-btn-danger qn-btn-md">{banner.cta}</Link>
                    )}
                </div>
            )}

            <section aria-label="Métricas del mes" className="grid4">
                <Stat
                    value={loading ? '…' : activeFlows}
                    label="Flujos activos"
                    hint={maxFlows ? `de ${maxFlows} disponibles en tu plan` : undefined}
                />
                <Stat
                    value={loading ? '…' : monthLogs.count.toLocaleString('es-AR')}
                    label="Ejecuciones del mes"
                    hint={monthLogs.okRate !== null ? `${monthLogs.okRate}% sin errores` : undefined}
                />
                <Stat
                    value={loading ? '…' : monthLogs.hours.toLocaleString('es-AR')}
                    unit="hs"
                    label="Horas ahorradas este mes"
                />
                <Stat
                    value={loading ? '…' : invoiceStats.done.toLocaleString('es-AR')}
                    label="Facturas emitidas este mes"
                    hint={invoiceStats.pending > 0 ? `${invoiceStats.pending} procesando` : undefined}
                />
            </section>

            <div className="resumenRow">
                <Card
                    title="Facturas por día"
                    meta={month}
                    actions={<span className="cardText">Promedio por día hábil: <b className="num" style={{ color: 'var(--text)' }}>{chart.avg}</b></span>}
                >
                    <div className="chart" role="img" aria-label={`Facturas completadas por día en ${month}`}>
                        <div className="chartAxis" aria-hidden="true">
                            <span>{chart.max}</span><span>{chart.max / 2}</span><span>0</span>
                        </div>
                        <div className="chartBody">
                            <div className="chartBars">
                                {chart.bars.map((b) => (
                                    <div
                                        key={b.day}
                                        className={`chartBar${b.isToday ? ' isToday' : ''}${b.isFuture ? ' isFuture' : ''}`}
                                        style={{ height: `${b.height}px` }}
                                        title={b.isFuture ? `Día ${b.day}` : `Día ${b.day}: ${b.value} facturas`}
                                    />
                                ))}
                            </div>
                            <div className="chartLabels" aria-hidden="true">
                                {chart.bars.map((b) => (
                                    <span key={b.day} className={b.isToday ? 'isToday' : undefined}>{b.label}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="chartLegend">
                        <span className="legendItem"><span className="legendSwatch" style={{ background: 'var(--chart-line)' }} />Facturas completadas</span>
                        <span className="legendItem"><span className="legendSwatch" style={{ background: 'var(--primary)' }} />Hoy</span>
                    </div>
                </Card>

                {plan && subscription ? (
                    <Card featured>
                        <div className="planHead">
                            <div className="metaItem">
                                <span className="overline">Tu plan</span>
                                <span className="planName">{plan.name}</span>
                            </div>
                            <StatusPill tone={subTone}>{subStatus}</StatusPill>
                        </div>
                        <Progress label="Flujos activos" value={activeFlows} max={maxFlows} />
                        <div className="planMeta">
                            <div className="metaItem">
                                <span className="metaLabel">Vence</span>
                                <span className="metaValue">{formatDate(subscription.vencimiento, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <div className="metaItem">
                                <span className="metaLabel">Precio</span>
                                <span className="metaValue">{formatMoney(plan.price_monthly)} / mes</span>
                            </div>
                        </div>
                    </Card>
                ) : (
                    <Card featured>
                        <span className="overline">Tu plan</span>
                        <p className="cardText">Todavía no tenés un plan asignado. Escribinos y lo activamos.</p>
                    </Card>
                )}

                <Card className="linkList">
                    <Link to="/dashboard/arca" className="linkRow">
                        <span className="linkRowText">
                            <span className="linkRowTitle">Conexión ARCA</span>
                            <span className="linkRowSub">Clave fiscal y certificados</span>
                        </span>
                        <StatusPill tone={arca.tone}>{arca.short}</StatusPill>
                    </Link>
                    <Link to="/dashboard/team" className="linkRow">
                        <span className="linkRowText">
                            <span className="linkRowTitle">Team</span>
                            <span className="linkRowSub">
                                {teamCount === 0 ? 'Solo vos tenés acceso' : `${teamCount} ${teamCount === 1 ? 'persona' : 'personas'} con acceso`}
                            </span>
                        </span>
                        <Icon name="chevron" className="muted" />
                    </Link>
                    <Link to="/dashboard/cuentas" className="linkRow">
                        <span className="linkRowText">
                            <span className="linkRowTitle">Cuentas linkeadas</span>
                            <span className="linkRowSub">{accountsSub}</span>
                        </span>
                        <Icon name="chevron" className="muted" />
                    </Link>
                </Card>
            </div>

            <div className="splitWide">
                <Card title="Facturas recientes" actions={<Link to="/dashboard/facturas" className="strongLink">Ver todas</Link>}>
                    <div className="tableWrap">
                        <table className="dataTable">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Concepto</th>
                                    <th className="right">Monto</th>
                                    <th>Estado</th>
                                    <th><span className="srOnly">Acción</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentInvoices.length === 0 ? (
                                    <tr><td colSpan="5" className="emptyRow">{loading ? 'Cargando facturas…' : 'Todavía no se generó ninguna factura.'}</td></tr>
                                ) : recentInvoices.map((inv) => {
                                    const st = invoiceTone(inv.status);
                                    return (
                                        <tr key={inv.id}>
                                            <td className="muted num nowrap">{formatDate(inv.created_at, { day: '2-digit', month: '2-digit' })}</td>
                                            <td>{inv.concepto || '-'}</td>
                                            <td className="right num nowrap">{formatMoney(inv.monto)}</td>
                                            <td><StatusPill tone={st.tone}>{st.label}</StatusPill></td>
                                            <td>
                                                <div className="cellActions">
                                                    {inv.status === 'completada' ? (
                                                        <Button size="sm" onClick={() => downloadInvoice(inv.file_path, notify)}>Descargar PDF</Button>
                                                    ) : inv.status === 'error' ? (
                                                        <Link to="/dashboard/facturas?estado=error" className="cellSub">Ver en Facturas</Link>
                                                    ) : (
                                                        <span className="cellSub">Generando…</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>

                <Card title="Actividad">
                    {activity.length === 0 ? (
                        <p className="cardText">{loading ? 'Cargando actividad…' : 'Tus flujos todavía no registraron actividad.'}</p>
                    ) : (
                        <ol className="activityList">
                            {activity.map((ev) => {
                                const ok = isLogOk(ev.status);
                                return (
                                    <li key={ev.id} className="activityItem">
                                        <span className={`activityDot${ok ? '' : ' isError'}`} aria-hidden="true" />
                                        <div className="activityBody">
                                            <div className="cellStack">
                                                <span className="activityText">{ev.details || ev.evento || '-'}</span>
                                                <span className="cellSub" style={ok ? undefined : { color: 'var(--danger)' }}>
                                                    {ok ? 'Ejecutado' : `Estado: ${ev.status}`}
                                                </span>
                                            </div>
                                            <span className="activityWhen">{formatWhen(ev.executed_at)}</span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </Card>
            </div>
        </div>
    );
}
